import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { formatNotificationDisplayMessage } from '../../utils/formatNotificationMessage';

import { useSessionStore } from '../../store/sessionStore';
import { computeUserProgress } from '../../utils/sessionProgress';

export interface UserProgress {
  login: boolean;
  welcome: boolean;
  details: boolean;
  firstPrompt: boolean;
  allPrompts: boolean;
  profilePic: boolean;
}

export interface ProfileCompletionBreakdownRow {
  section: string;
  label: string;
  weight: number;
  earned: number;
  sectionPercent: number;
}

export interface ProgressResult {
  percent: number;
  breakdown: { step: keyof UserProgress; done: boolean; weight: number }[];
  sections: ProfileCompletionBreakdownRow[];
  suggestions: string[];
}

export const fetchUserProgress = async (): Promise<ProgressResult> => {
  const { currentUser, prompts, portfolio } = useSessionStore.getState();
  const user = currentUser as Record<string, unknown> | null;
  return computeUserProgress(user, prompts, portfolio as Record<string, unknown> | null);
};

export const fetchUserData = async (options?: { force?: boolean }) => {
  try {
    return await useSessionStore.getState().refreshUser(options);
  } catch (error: unknown) {
    console.error('Error fetching user data:', error);
    return null;
  }
};

export type AppNotification = {
  id: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  type?: string;
  action_url?: string;
};

function normalizeNotifications(raw: unknown): AppNotification[] {
  let list: Record<string, unknown>[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw && typeof raw === 'object') {
    const envelope = raw as Record<string, unknown>;
    if (Array.isArray(envelope.data)) {
      list = envelope.data as Record<string, unknown>[];
    } else if (Array.isArray(envelope.results)) {
      list = envelope.results as Record<string, unknown>[];
    }
  }

  return list.map((n) => {
    const type = n.type != null ? String(n.type) : undefined;
    const name = n.name != null ? String(n.name) : undefined;
    const rawMessage = String(n.message ?? '');
    return {
      id: String(n.id ?? ''),
      message: formatNotificationDisplayMessage({
        message: rawMessage,
        type,
        name,
      }),
      isRead: Boolean(n.isRead ?? n.is_read ?? false),
      createdAt: String(n.createdAt ?? n.created_at ?? ''),
      type,
      action_url: n.action_url != null ? String(n.action_url) : undefined,
    };
  });
}

export const fetchUserNotificationData = async (): Promise<AppNotification[] | null> => {
  try {
    const response = await axiosInstance.get(API_ROUTES.NOTIFICATION.GET_NOTIFICATION);
    return normalizeNotifications(response.data);
  } catch (error: any) {
    console.error('Error fetching user notification data:', error);
    return null;
  }
};

export const markNotificationAsRead = async (
  notificationId: string,
  options?: { message?: string }
) => {
  const url = `${API_ROUTES.NOTIFICATION.GET_NOTIFICATION}/${notificationId}/read`;

  const requestMarkRead = async (targetUrl: string) => {
    try {
      return await axiosInstance.post(targetUrl);
    } catch {
      return await axiosInstance.patch(targetUrl);
    }
  };

  try {
    const response = await requestMarkRead(url);
    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    const staleMessage = options?.message?.trim();
    if (status !== 404 || !staleMessage) {
      console.error('Error marking notification as read:', error);
      throw error;
    }

    const refreshed = await fetchUserNotificationData();
    const match = refreshed?.find((item) => item.message === staleMessage && !item.isRead);
    if (!match?.id || match.id === notificationId) {
      console.error('Error marking notification as read:', error);
      throw error;
    }

    const response = await requestMarkRead(
      `${API_ROUTES.NOTIFICATION.GET_NOTIFICATION}/${match.id}/read`
    );
    return response.data;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const response = await axiosInstance.post(`${API_ROUTES.NOTIFICATION.MARK_ALL_AS_READ}`);

    return response.data;
  } catch (error: any) {
    console.error('Error marking all notifications as read:', error);
  }
};

export const deleteNotification = async (notificationId: string) => {
  try {
    const response = await axiosInstance.delete(
      `${API_ROUTES.NOTIFICATION.GET_NOTIFICATION}/${notificationId}`
    );

    return response.data;
  } catch (error: any) {
    console.error('Error deleting notification:', error);
  }
};

export const deleteUserAccount = async () => {
  try {
    const response = await axiosInstance.delete(API_ROUTES.USER.DELETE_USER);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const deactivateUserAccount = async () => {
  try {
    const response = await axiosInstance.post(API_ROUTES.USER.DEACTIVATE_USER);
    return response.data;
  } catch (error: any) {
    console.error('Error deactivating user:', error);
    throw error;
  }
};

export const deleteUserAccountAdmin = async (userID: string) => {
  try {
    const response = await axiosInstance.delete(`${API_ROUTES.USER.ADMIN_DELETE}/${userID}`);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting user:', error);
    throw error;
  }
};
